import { ConnectionType, deviceApi } from '@accedo/ctv-device-api';
import { logger } from '@accedo/logger';
import { getDeviceInformation } from '##/utils/device';
import generateUUID from '##/utils/generateUUID';
import { LiveCarouselItem } from '##/components/LiveCarousel/types';
import { LiveChannel } from '##/services/tenApi/collections/types';
import { version } from '../../../../package.json';

const OZTAM_DEVICE_ID_KEY = 'oztam-device-id';
const OZTAM_DEVICE_ID_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const roundPosition = (pos: number): number => Math.round(pos * 10) / 10;

const getLocalStorageDeviceId = (): string => {
  try {
    return localStorage.getItem(OZTAM_DEVICE_ID_KEY) || '';
  } catch (err) {
    logger.warn('[getOzTamDeviceId] Local storage is unavailable:', err);

    return '';
  }
};

const setLocalStorageDeviceId = (deviceId: string): boolean => {
  try {
    localStorage.setItem(OZTAM_DEVICE_ID_KEY, deviceId);
    return localStorage.getItem(OZTAM_DEVICE_ID_KEY) === deviceId;
  } catch (err) {
    logger.warn('[getOzTamDeviceId] Failed to store ID locally:', err);

    return false;
  }
};

const getCookieDeviceId = (): string => {
  try {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    const cookiePrefix = `${OZTAM_DEVICE_ID_KEY}=`;

    for (let idx = 0; idx < cookies.length; idx += 1) {
      const cookie = cookies[idx].trim();

      if (cookie.indexOf(cookiePrefix) === 0) {
        return decodeURIComponent(cookie.substring(cookiePrefix.length));
      }
    }
  } catch (err) {
    logger.warn('[getOzTamDeviceId] Cookies are unavailable:', err);
  }

  return '';
};

const setCookieDeviceId = (deviceId: string): void => {
  try {
    document.cookie = `${OZTAM_DEVICE_ID_KEY}=${encodeURIComponent(
      deviceId,
    )}; Max-Age=${OZTAM_DEVICE_ID_COOKIE_MAX_AGE_SECONDS}; path=/`;
  } catch (err) {
    logger.warn('[getOzTamDeviceId] Failed to store ID in a cookie:', err);
  }
};

export const getOzTamDeviceId = async (): Promise<string> => {
  const localStorageDeviceId = getLocalStorageDeviceId();

  if (UUID_PATTERN.test(localStorageDeviceId)) {
    logger.info(
      '[getOzTamDeviceId] Using device ID from local storage:',
      localStorageDeviceId,
    );
    return localStorageDeviceId;
  }

  // Cookie fallback for older HbbTV devices.
  const cookieDeviceId = getCookieDeviceId();

  if (UUID_PATTERN.test(cookieDeviceId)) {
    setCookieDeviceId(cookieDeviceId);
    setLocalStorageDeviceId(cookieDeviceId);
    logger.info(
      '[getOzTamDeviceId] Using device ID from cookie fallback:',
      cookieDeviceId,
    );
    return cookieDeviceId;
  }

  const deviceId = generateUUID();

  if (!setLocalStorageDeviceId(deviceId)) {
    logger.warn(
      '[getOzTamDeviceId] Failed to store ID locally, falling back to cookie storage',
    );
    setCookieDeviceId(deviceId);
  }

  logger.info('[getOzTamDeviceId] oztamDeviceId:', deviceId);
  return deviceId;
};

export const getVendorVersion = async (): Promise<string> =>
  `Tenplay_HbbTV_${version}`;

export const getConnectionType = async (): Promise<ConnectionType | ''> => {
  const data = await getDeviceInformation();
  const { platform } = data;
  const deviceConnection = await deviceApi.network?.connectionType();
  if (deviceConnection && platform === 'iQ3') {
    return deviceConnection;
  }
  return '';
};

export const getOztamLiveMediaId = (
  liveVideo: LiveCarouselItem | LiveChannel,
): string => {
  let mediaId = 'LIVEID';
  const playbackEndpoint = liveVideo?.streamUrl;
  const isTenShake = [
    (liveVideo as LiveCarouselItem)?.key?.toLowerCase(),
    (liveVideo as LiveChannel).key?.toLowerCase(),
  ].includes('tenshake');

  if (isTenShake && (liveVideo as LiveChannel)?.publisherId) {
    mediaId = (liveVideo as LiveChannel)?.publisherId;
  } else if (playbackEndpoint) {
    const idOnEndpoint = playbackEndpoint.substring(
      playbackEndpoint.lastIndexOf('/') + 1,
    );
    if (idOnEndpoint) {
      mediaId = idOnEndpoint;
    }
  }

  return mediaId;
};
